// src/pages/settings/components/AppearanceSettings.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  Button,
  Divider,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  SettingsBrightness as AutoModeIcon,
  FormatTextdirectionLToR as LTRIcon,
  FormatTextdirectionRToL as RTLIcon,
  Translate as TranslateIcon,
  Palette as PaletteIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

// Import stores
import useSettingsStore from '../../../stores/settingsStore';
import useNotificationStore from '../../../stores/notificationStore';

// Import color palettes
import { colorPalettes } from '../../../theme/colors';

const AppearanceSettings = () => {
  const theme = useTheme();
  const { settings, updateSetting } = useSettingsStore();
  const { showSuccess } = useNotificationStore();

  // Local state to track changes
  const [themeMode, setThemeMode] = useState(settings.theme || 'light');
  const [direction, setDirection] = useState(settings.direction || 'ltr');
  const [language, setLanguage] = useState(settings.language || 'en');
  const [hideAnimations, setHideAnimations] = useState(settings.hideAnimations || false);

  // Get the current color palette from settings or default
  const [selectedPalette, setSelectedPalette] = useState(settings.colorPalette || 'original');

  // Apply settings to the page
  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', language);
  }, [direction, language]);

  // Handle theme change
  const handleThemeChange = (event) => {
    setThemeMode(event.target.value);
  };

  // Handle direction change
  const handleDirectionChange = (event) => {
    setDirection(event.target.value);
  };

  // Handle language change
  const handleLanguageChange = (event) => {
    setLanguage(event.target.value);
  };

  // Handle animations toggle
  const handleAnimationsToggle = (event) => {
    setHideAnimations(event.target.checked);
  };

  // Handle palette selection
  const handlePaletteSelect = (paletteKey) => {
    setSelectedPalette(paletteKey);
  };

  // Save all appearance settings
  const handleSaveSettings = async () => {
    try {
      // Save all settings including theme mode and color palette
      await updateSetting('theme', themeMode);
      await updateSetting('direction', direction);
      await updateSetting('language', language);
      await updateSetting('hideAnimations', hideAnimations);
      await updateSetting('colorPalette', selectedPalette);
      
      showSuccess('Appearance settings saved successfully');
      
      // The theme will update automatically since App.jsx is reactive to settings changes
      // No need to reload the page
    } catch (error) {
      console.log('Failed to save appearance settings:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Appearance Settings</Typography>
        <Button 
          variant="contained" 
           
          onClick={handleSaveSettings}
        >
          Save Changes
        </Button>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      <Grid container spacing={4}>
        {/* Theme Mode Selection */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, }}>
              {themeMode === 'light' && <LightModeIcon sx={{ mr: 1, color: theme.palette.primary.main }} />}
              {themeMode === 'dark' && <DarkModeIcon sx={{ mr: 1, color: theme.palette.primary.main }} />}
              {themeMode === 'system' && <AutoModeIcon sx={{ mr: 1, color: theme.palette.primary.main }} />}
              <Typography variant="subtitle1">Theme Mode</Typography>
            </Box>
            
            <FormControl component="fieldset">
              <RadioGroup
                value={themeMode}
                onChange={handleThemeChange}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <Paper 
                      elevation={themeMode === 'light' ? 3 : 1}
                      sx={{ 
                        p: 2, 
                        bgcolor: themeMode === 'light' ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
                        border: themeMode === 'light' ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                        borderRadius: 2,
                        cursor: 'pointer'
                      }}
                      onClick={() => setThemeMode('light')}
                    >
                      <FormControlLabel
                        value="light"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LightModeIcon sx={{ mr: 1 }} />
                            Light
                          </Box>
                        }
                        sx={{ m: 0 }}
                      />
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} sm={12}>
                    <Paper 
                      elevation={themeMode === 'dark' ? 3 : 1}
                      sx={{ 
                        p: 2, 
                        bgcolor: themeMode === 'dark' ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
                        border: themeMode === 'dark' ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                        borderRadius: 2,
                        cursor: 'pointer'
                      }}
                      onClick={() => setThemeMode('dark')}
                    >
                      <FormControlLabel
                        value="dark"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <DarkModeIcon sx={{ mr: 1 }} />
                            Dark
                          </Box>
                        }
                        sx={{ m: 0 }}
                      />
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} sm={12}>
                    <Paper 
                      elevation={themeMode === 'system' ? 3 : 1}
                      sx={{ 
                        p: 2, 
                        bgcolor: themeMode === 'system' ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
                        border: themeMode === 'system' ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
                        borderRadius: 2,
                        cursor: 'pointer'
                      }}
                      onClick={() => setThemeMode('system')}
                    >
                      <FormControlLabel
                        value="system"
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AutoModeIcon sx={{ mr: 1 }} />
                            System
                          </Box>
                        }
                        sx={{ m: 0 }}
                      />
                    </Paper>
                  </Grid>
                </Grid>
              </RadioGroup>
            </FormControl>
          </Paper>
        </Grid>
        
        {/* Direction and Language Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Text Direction
            </Typography>
            
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <RadioGroup
                row
                value={direction}
                onChange={handleDirectionChange}
              >
                <FormControlLabel 
                  value="ltr" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LTRIcon sx={{ mr: 1 }} />
                      Left to Right
                    </Box>
                  } 
                />
                <FormControlLabel 
                  value="rtl" 
                  control={<Radio />} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <RTLIcon sx={{ mr: 1 }} />
                      Right to Left
                    </Box>
                  } 
                />
              </RadioGroup>
            </FormControl>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <TranslateIcon sx={{ mr: 1 }} />
              Language
            </Typography>
            
            <FormControl component="fieldset">
              <RadioGroup
                value={language}
                onChange={handleLanguageChange}
              >
                <FormControlLabel value="en" control={<Radio />} label="English" />
                <FormControlLabel value="ar" control={<Radio />} label="العربية (Arabic)" />
              </RadioGroup>
            </FormControl>
          </Paper>
        </Grid>
        
        {/* Color Palettes */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PaletteIcon sx={{ mr: 1 }} />
              Color Palette
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {Object.entries(colorPalettes).map(([key, palette]) => (
                <Grid item xs={6} sm={4} md={2} key={key}>
                  <Tooltip title={palette.name} placement="top">
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        cursor: 'pointer',
                        position: 'relative',
                        p: 1,
                        borderRadius: 1,
                        border: selectedPalette === key ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.background.default, 0.5),
                        },
                      }}
                      onClick={() => handlePaletteSelect(key)}
                    >
                      {selectedPalette === key && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            bgcolor: theme.palette.primary.main,
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckIcon sx={{ color: 'white', fontSize: 16 }} />
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', mb: 1 }}>
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            bgcolor: palette.primary,
                            borderTopLeftRadius: 4,
                            borderBottomLeftRadius: 4,
                          }}
                        />
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            bgcolor: palette.secondary,
                          }}
                        />
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            bgcolor: palette.accent,
                            borderTopRightRadius: 4,
                            borderBottomRightRadius: 4,
                          }}
                        />
                      </Box>
                      <Typography variant="caption" noWrap sx={{ maxWidth: '100%' }}>
                        {palette.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        
        {/* Additional Options */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Additional Options
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
              <Typography variant="body2">
                Reduce animations
              </Typography>
              <Switch
                checked={hideAnimations}
                onChange={handleAnimationsToggle}
                inputProps={{ 'aria-label': 'Reduce animations' }}
              />
            </Box>
            <Typography variant="caption" color="textSecondary">
              Reduces motion for users who prefer less animation
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AppearanceSettings;